import { useCallback, useState } from 'react';
import { ColumnConfig } from '@/components/ui/data-table/config';
import { isDeepEqual } from 'remeda';

const STORAGE_PREFIX = 'table-config';

function getStorageKey(tableId: string): string {
  return `${STORAGE_PREFIX}:${tableId}`;
}

function loadConfig(tableId: string): ColumnConfig[] | null {
  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveConfig(tableId: string, config: ColumnConfig[]): void {
  try {
    localStorage.setItem(getStorageKey(tableId), JSON.stringify(config));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

function clearConfig(tableId: string): void {
  try {
    localStorage.removeItem(getStorageKey(tableId));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

export function usePersistedTableConfig(
  tableId: string,
  initialConfig: ColumnConfig[],
): [ColumnConfig[], (config: ColumnConfig[] | null) => void] {
  const [config, setConfigState] = useState<ColumnConfig[]>(() => {
    const stored = loadConfig(tableId);
    return stored ?? initialConfig;
  });

  const setConfig = useCallback(
    (newConfig: ColumnConfig[] | null) => {
      // console.log('setConfig', {
      //   newConfig: simpleHash(JSON.stringify(newConfig)) % 100,
      //   config: simpleHash(JSON.stringify(config)) % 100,
      //   initialConfig: simpleHash(JSON.stringify(initialConfig)) % 100,
      // });
      if (newConfig === null || isDeepEqual(newConfig, initialConfig)) {
        clearConfig(tableId);
        setConfigState(initialConfig);
      } else if (!isDeepEqual(newConfig, config)) {
        saveConfig(tableId, newConfig);
        setConfigState(newConfig);
      }
    },
    [config, tableId, initialConfig],
  );

  return [config, setConfig];
}
